// ============================================================
// STORY MODE — Canvas avatar renderer
// ============================================================
//
// Draws character portraits using only canvas primitives — no
// image assets needed. Each speaker has a distinct shape +
// colour palette so they read at a glance:
//   - jiang     灰袍 + 白鬚 (square frame)
//   - nezha     紅袍 + 火焰光暈 (circle frame)
//   - moliHong  深紅 + 鋸齒尖角 (diamond frame)
//   - narrator  半透明灰 (placeholder)
//
// Designed to slot into a fixed-size square (default 80×80).
// All drawing relative to (cx, cy) and `size` so callers can
// position it anywhere. State-free: no instance, just helpers.
// ============================================================

import type { SpeakerId } from './StoryTypes.ts';
import { ctx } from '../canvas.ts';

interface PaletteSwatch {
  /** Background fill */
  bg: string;
  /** Frame stroke */
  frame: string;
  /** Body / robe colour */
  body: string;
  /** Accent (hair, weapon, gem) */
  accent: string;
  /** Skin tone */
  skin: string;
}

const PALETTES: Record<SpeakerId, PaletteSwatch> = {
  jiang: {
    bg: '#1a1f2c',
    frame: '#bbaa66',
    body: '#5a5a5a',
    accent: '#eeeeee',
    skin: '#d4b08a',
  },
  nezha: {
    bg: '#2a1414',
    frame: '#ff8844',
    body: '#cc2222',
    accent: '#ffcc44',
    skin: '#f5d0a0',
  },
  moliHong: {
    bg: '#2a0a0a',
    frame: '#aa1133',
    body: '#660011',
    accent: '#ffcc66',
    skin: '#a8806a',
  },
  narrator: {
    bg: '#1a1a22',
    frame: '#556677',
    body: '#444',
    accent: '#888',
    skin: '#666',
  },
};

/** Localised speaker display names (defaults if dialogue line doesn't override). */
const NAMES: Record<SpeakerId, string> = {
  jiang: '姜子牙',
  nezha: '哪吒',
  moliHong: '魔禮紅',
  narrator: '旁白',
};

export function speakerName(id: SpeakerId): string {
  return NAMES[id] || id;
}

/**
 * Draw the avatar for `speaker` centred at (cx, cy) inside a square
 * of `size` px. Frame shape varies by speaker so portraits are
 * distinguishable even if you forget the colours.
 */
export function drawAvatar(speaker: SpeakerId, cx: number, cy: number, size: number): void {
  const p = PALETTES[speaker] || PALETTES.narrator;
  const half = size / 2;
  const r = half - 2;

  // Frame background — shape per speaker
  ctx.save();
  ctx.fillStyle = p.bg;
  ctx.strokeStyle = p.frame;
  ctx.lineWidth = 2;
  if (speaker === 'jiang') {
    // Square frame
    ctx.beginPath();
    ctx.rect(cx - r, cy - r, r * 2, r * 2);
    ctx.fill(); ctx.stroke();
  } else if (speaker === 'nezha') {
    // Circle frame
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
  } else if (speaker === 'moliHong') {
    // Diamond frame
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - r, cy);
    ctx.closePath();
    ctx.fill(); ctx.stroke();
  } else {
    // Narrator — rounded rect placeholder
    ctx.beginPath();
    ctx.rect(cx - r, cy - r, r * 2, r * 2);
    ctx.fill(); ctx.stroke();
  }
  ctx.restore();

  // Character body / face — clip to frame
  ctx.save();
  ctx.beginPath();
  if (speaker === 'nezha') {
    ctx.arc(cx, cy, r - 1, 0, Math.PI * 2);
  } else if (speaker === 'moliHong') {
    ctx.moveTo(cx, cy - r + 1);
    ctx.lineTo(cx + r - 1, cy);
    ctx.lineTo(cx, cy + r - 1);
    ctx.lineTo(cx - r + 1, cy);
    ctx.closePath();
  } else {
    ctx.rect(cx - r + 1, cy - r + 1, r * 2 - 2, r * 2 - 2);
  }
  ctx.clip();

  // Robe / body (lower half)
  ctx.fillStyle = p.body;
  ctx.fillRect(cx - r, cy - r * 0.05, r * 2, r * 1.1);

  // Head (skin tone circle in upper portion)
  const headR = r * 0.4;
  ctx.fillStyle = p.skin;
  ctx.beginPath();
  ctx.arc(cx, cy - r * 0.25, headR, 0, Math.PI * 2);
  ctx.fill();

  // Speaker-specific accents
  if (speaker === 'jiang') {
    // 白鬚 — flowing beard below head
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.moveTo(cx - headR * 0.55, cy - r * 0.05);
    ctx.quadraticCurveTo(cx, cy + r * 0.45, cx + headR * 0.55, cy - r * 0.05);
    ctx.quadraticCurveTo(cx, cy + r * 0.15, cx - headR * 0.55, cy - r * 0.05);
    ctx.closePath();
    ctx.fill();
    // 髮冠 (small rect on top)
    ctx.fillStyle = '#444';
    ctx.fillRect(cx - headR * 0.6, cy - r * 0.6, headR * 1.2, headR * 0.35);
  } else if (speaker === 'nezha') {
    // 火焰光暈 — small flame tongues around head
    ctx.fillStyle = p.accent;
    for (let i = 0; i < 5; i++) {
      const ang = -Math.PI / 2 + (i - 2) * 0.5;
      const fx = cx + Math.cos(ang) * (headR + 4);
      const fy = cy - r * 0.25 + Math.sin(ang) * (headR + 4);
      ctx.beginPath();
      ctx.moveTo(fx, fy - 5);
      ctx.lineTo(fx - 3, fy + 3);
      ctx.lineTo(fx + 3, fy + 3);
      ctx.closePath();
      ctx.fill();
    }
    // 髮髻 (top knot)
    ctx.fillStyle = '#221';
    ctx.beginPath();
    ctx.arc(cx, cy - r * 0.55, headR * 0.4, 0, Math.PI * 2);
    ctx.fill();
    // 乾坤圈 hint — small gold ring on chest
    ctx.strokeStyle = p.accent;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy + r * 0.35, r * 0.18, 0, Math.PI * 2);
    ctx.stroke();
  } else if (speaker === 'moliHong') {
    // 兇惡眉 + 髯 (angry brow & beard)
    ctx.fillStyle = '#1a0808';
    // Brow
    ctx.fillRect(cx - headR * 0.7, cy - r * 0.35, headR * 0.5, headR * 0.15);
    ctx.fillRect(cx + headR * 0.2, cy - r * 0.35, headR * 0.5, headR * 0.15);
    // Beard — wider, blocky
    ctx.fillStyle = '#220808';
    ctx.beginPath();
    ctx.moveTo(cx - headR * 0.7, cy - r * 0.05);
    ctx.lineTo(cx + headR * 0.7, cy - r * 0.05);
    ctx.lineTo(cx + headR * 0.5, cy + r * 0.25);
    ctx.lineTo(cx - headR * 0.5, cy + r * 0.25);
    ctx.closePath();
    ctx.fill();
    // 寶石飾 — small gem on chest
    ctx.fillStyle = p.accent;
    ctx.beginPath();
    ctx.moveTo(cx, cy + r * 0.15);
    ctx.lineTo(cx + 5, cy + r * 0.3);
    ctx.lineTo(cx, cy + r * 0.45);
    ctx.lineTo(cx - 5, cy + r * 0.3);
    ctx.closePath();
    ctx.fill();
  } else {
    // narrator — soft "?" mark
    ctx.fillStyle = '#888';
    ctx.font = `bold ${Math.floor(headR * 1.4)}px sans-serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('?', cx, cy - r * 0.25);
  }
  ctx.restore();
}
